import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AxsIconComponent } from '@vanguard/axs-angular';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { SharedInfoService } from 'src/app/shared/services/shared-info/shared-info.service';
import { content } from 'src/assets/content/fallback-content';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { AxsInputNumberComponent } from '../../shared/axs/input-number/input-number.component';
import { GatekeeperService } from '../../shared/services/gatekeeper/gatekeeper.service';
import { ClearAllFieldsButtonComponent } from '../clear-all-fields-button/clear-all-fields-button.component';

@Component({
  selector: 'roth-conv-details',
  standalone: true,
  imports: [CommonModule, AxsInputNumberComponent, AxsIconComponent, ClearAllFieldsButtonComponent],
  templateUrl: './roth-conv-details.component.html',
  styleUrl: './roth-conv-details.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class RothConvDetailsComponent implements OnInit, OnDestroy {
  rothConvDetails: any = content.Data.v4_rcc_root.content.rothConvDetails;
  errorsContent: any = content.Data.v4_rcc_root.content.error;

  stateTaxRatesFromBXR: boolean = false;

  // AC3 hint: how much room is left in the current federal bracket once the conversion is added to
  // income. Recomputed from the service, which owns the combined-income math.
  rothConversionAmountHelperText: string = '';

  private readonly unsubscribe = new Subject<void>();

  @Output() infoIconClicked = new EventEmitter<{ accordion: string; fieldId: string }>();

  _rothConversionAmount: any = 0;
  _yearsUntilDist: any = 0;
  _paymentMethod: string = 'ira';
  _annualReturn: any = 0;
  _cashYield: any = 0;
  _dividendTaxRate: any = 0;
  _capitalGainsTaxRate: any = 0;
  _dividendYield: any = 0;
  _percentOfIra: any = 0;

  defaultDividendTaxRate: string = '';
  defaultCapitalGainsTaxRate: string = '';

  _rothConversionAmountErrorMessage: string = '';
  _yearsUntilDistErrorMessage: string = '';
  _annualReturnErrorMessage: string = '';
  _cashYieldErrorMessage: string = '';
  _dividendTaxRateErrorMessage: string = '';
  _capitalGainsTaxRateErrorMessage: string = '';
  _dividendYieldErrorMessage: string = '';
  _percentOfIraErrorMessage: string = '';

  rothConversionAmountValid: boolean = true;
  yearsUntilDistValid: boolean = true;
  annualReturnValid: boolean = true;
  cashYieldValid: boolean = true;
  dividendTaxRateValid: boolean = true;
  capitalGainsTaxRateValid: boolean = true;
  dividendYieldValid: boolean = true;
  percentOfIraValid: boolean = true;

  rothConversionAmountMin: number = 1;
  rothConversionAmountMax: number = 10000000;
  yearsUntilDistMin: number = 1;
  yearsUntilDistMax: number = 50;
  annualReturnMin: number = 1;
  annualReturnMax: number = 50;
  cashYieldMin: number = 0;
  cashYieldMax: number = 10;
  dividendTaxRateMin: number = 0;
  dividendTaxRateMax: number = 100;
  capitalGainsTaxRateMin: number = 0;
  capitalGainsTaxRateMax: number = 100;
  dividendYieldMin: number = 0;
  dividendYieldMax: number = 10;
  percentOfIraMin: number = 0;
  percentOfIraMax: number = 100;

  paymentMethodDropdownOptions: any;

  // eslint-disable-next-line max-params
  constructor(
    public sharedInfoService: SharedInfoService,
    private readonly contentService: ContentService,
    private readonly adobeLaunchService: AdobeLaunchService,
    private readonly gatekeeperService: GatekeeperService,
  ) {}

  ngOnInit(): void {
    this.stateTaxRatesFromBXR = this.gatekeeperService.getToggleValue('STATE_TAX_RATES');
    this.paymentMethodDropdownOptions = this.buildPaymentMethodOptions();

    this.contentService
      .getContent()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => {
        this.rothConvDetails = data.Data.v4_rcc_root.content.rothConvDetails;
        this.errorsContent = data.Data.v4_rcc_root.content.error;
        // Dropdown labels are copied out of the content, so rebuild them alongside it.
        this.paymentMethodDropdownOptions = this.buildPaymentMethodOptions();
      });

    this.initializeValues();
  }

  private buildPaymentMethodOptions(): { label: string; value: string }[] {
    return [
      { label: this.rothConvDetails?.cash, value: 'cash' },
      { label: this.rothConvDetails?.taxInefficient, value: 'tax-inefficient' },
      { label: this.rothConvDetails?.taxEfficient, value: 'tax-efficient' },
      { label: this.rothConvDetails?.ira, value: 'ira' },
    ];
  }

  private initializeValues(): void {
    // Subscribe to sample client load events to clear all errors
    this.sharedInfoService
      .getSampleClientLoaded$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.clearAllErrors();
      });

    // Desktop and the mobile sheet each render this component, so both have to blank on either's click.
    this.sharedInfoService
      .getInputsCleared$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((section) => {
        if (section === 'rothConversion') {
          this.blankFields();
        }
      });

    this.sharedInfoService
      .getRothConversionAmount$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._rothConversionAmount = value;
      });

    this.sharedInfoService
      .getYearsUntilDist$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._yearsUntilDist = value;
      });

    this.sharedInfoService
      .getHowWillYouPay$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => (this._paymentMethod = value));

    this.sharedInfoService
      .getAnnualReturn$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._annualReturn = value;
      });

    this.sharedInfoService
      .getCashYield$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._cashYield = value;
      });

    this.sharedInfoService
      .getTaxChartDividendTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.defaultDividendTaxRate = value.toString();
        this.onDividendTaxRateChange(value);
      });

    this.sharedInfoService
      .getDividendTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._dividendTaxRate = value;
      });

    this.sharedInfoService
      .getTaxChartCapitalGainsTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.defaultCapitalGainsTaxRate = value.toString();
        this.onCapitalGainsTaxRateChange(value);
      });

    this.sharedInfoService
      .getCapitalGainsTaxRate$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._capitalGainsTaxRate = value;
      });

    this.sharedInfoService
      .getDividendYield$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._dividendYield = value;
      });

    this.sharedInfoService
      .getPercentOfIra$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this._percentOfIra = value;
      });

    // The AC3 hint depends on the annual taxable income owned by ClientTaxInfoComponent, so it arrives via
    // the service. Filing status is here too because it selects the bracket table the hint is measured
    // against. Display only - never write back from here, or the emission re-enters as a loop.
    this.sharedInfoService
      .getCombinedCurrentIncome$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.setRothConversionAmountHelperText();
      });

    this.sharedInfoService
      .getFilingStatus$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.setRothConversionAmountHelperText();
      });
  }

  // eslint-disable-next-line max-statements
  private clearAllErrors(): void {
    // Reset all validation flags to valid
    this.rothConversionAmountValid = true;
    this.yearsUntilDistValid = true;
    this.annualReturnValid = true;
    this.cashYieldValid = true;
    this.dividendTaxRateValid = true;
    this.capitalGainsTaxRateValid = true;
    this.dividendYieldValid = true;
    this.percentOfIraValid = true;

    // Clear all error messages
    this._rothConversionAmountErrorMessage = '';
    this._yearsUntilDistErrorMessage = '';
    this._annualReturnErrorMessage = '';
    this._cashYieldErrorMessage = '';
    this._dividendTaxRateErrorMessage = '';
    this._capitalGainsTaxRateErrorMessage = '';
    this._dividendYieldErrorMessage = '';
    this._percentOfIraErrorMessage = '';
  }

  onClearAllFields(): void {
    this.sharedInfoService.clearSection('rothConversion');
    this.resetConditionalFields();
    this.adobeLaunchService.captureCtaButton('roth-calculator:clear-all-fields', 'betr calculator');
  }

  private blankFields(): void {
    this.clearAllErrors();
    this._rothConversionAmount = '';
    this._yearsUntilDist = '';
    this._paymentMethod = '';
    this._annualReturn = '';
    this._percentOfIra = '';
    this.setRothConversionAmountHelperText();
  }

  /**
   * The payment method's conditional fields are hidden rather than blanked, so they reappear with their
   * defaults once a method is picked. Written to the service too, since that's what gets calculated.
   */
  private resetConditionalFields(): void {
    const defaults = this.sharedInfoService.defaultInputData;
    this.sharedInfoService.setCashYield(defaults.cashYield);
    this.sharedInfoService.setDividendYield(defaults.dividendYield);
    // The tax rates default to the bracket-derived rates, not the sample client's.
    this.sharedInfoService.setDividendTaxRate(Number(this.defaultDividendTaxRate));
    this.sharedInfoService.setCapitalGainsTaxRate(Number(this.defaultCapitalGainsTaxRate));
  }

  infoIconOnClick(accordion: string, fieldId: string): void {
    this.infoIconClicked.emit({ accordion, fieldId });
    this.adobeLaunchService.captureAction(
      'roth-calculator:terms-and-definitions',
      'link',
      'betr calculator',
    );
  }

  rothConversionAmountClicked() {
    this.adobeLaunchService.captureAction(
      'roth-calculator:roth-conversion-amount',
      'input',
      'betr calculator',
    );
  }

  onRothConversionAmountChange(value: number): void {
    this._rothConversionAmount = value;
    this.rothConversionAmountValid =
      !isNaN(value) &&
      value >= this.rothConversionAmountMin &&
      value <= this.rothConversionAmountMax;

    // A blank or out-of-range amount counts as zero toward the tax brackets and the combined-income
    // helper texts. Must be set before the rate lookups below, which read it.
    this.sharedInfoService.setEffectiveRothConversionAmount(
      this.rothConversionAmountValid ? value : 0,
    );

    if (this.rothConversionAmountValid) {
      this._rothConversionAmountErrorMessage = '';
      this.sharedInfoService.removeInputError('Roth conversion amount');
      this.sharedInfoService.setRothConversionAmount(value);
      this.refreshDefaultFederalTaxRates();
    } else {
      this._rothConversionAmountErrorMessage = this.errorsContent?.['rothamounterror'];
      this.sharedInfoService.addInputError('Roth conversion amount');
    }

    // Recomputed in both branches so an invalid amount cannot leave a stale figure on screen.
    this.setRothConversionAmountHelperText();
    this.adobeLaunchService.captureAction('task complete', 'text', 'betr calculator');
  }

  /**
   * AC1: a Roth conversion is ordinary income in the year it happens, so changing the amount moves the
   * default federal bracket for both the current and future rates. Called from the change handler rather
   * than a subscription so the write stays out of the service's emission path. Gated on the toggle -
   * with it off, changing the conversion amount must not touch the rate fields at all.
   */
  private refreshDefaultFederalTaxRates(): void {
    if (!this.stateTaxRatesFromBXR) {
      return;
    }

    // Writing a rate for a cleared income or filing status would refill the other tab's field.
    if (this.sharedInfoService.canDeriveFederalRate(false)) {
      this.sharedInfoService.setCurrentFederalTaxRate(
        this.sharedInfoService.getCurrentTaxRateBasedOnCurrentYear(),
      );
    }
    if (this.sharedInfoService.canDeriveFederalRate(true)) {
      this.sharedInfoService.setFutureFederalTaxRate(
        this.sharedInfoService.getFutureTaxRateBasedOnCurrentYear(),
      );
    }
  }

  setRothConversionAmountHelperText(): void {
    // The distance-to-next-bracket hint lived on the annual taxable income field before the toggle, so
    // this field keeps its static payment-method hint when the toggle is off.
    if (!this.stateTaxRatesFromBXR) {
      this.rothConversionAmountHelperText = '';
      return;
    }

    const amount = this.sharedInfoService.getAmountUntilNextTaxBracketBasedOnCurrentYear();

    if (amount === null) {
      this.rothConversionAmountHelperText = this.rothConvDetails?.['highestTaxBracket'] ?? '';
      return;
    }

    this.rothConversionAmountHelperText = (
      this.rothConvDetails?.['nextHigherTaxBracket'] ?? ''
    ).replace(
      '$YYY',
      amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    );
  }

  onYearsUntilDistChange(value: number): void {
    this._yearsUntilDist = value;
    if (isNaN(value) || value < this.yearsUntilDistMin) {
      this.yearsUntilDistValid = false;
      this._yearsUntilDistErrorMessage = this.errorsContent?.['rothyearerror'];
      this.sharedInfoService.addInputError('Years until distribution');
    } else if (value > this.yearsUntilDistMax) {
      this.yearsUntilDistValid = false;
      this._yearsUntilDistErrorMessage = this.errorsContent?.['rothyearerror'];
      this.sharedInfoService.addInputError('Years until distribution');
    } else {
      this.yearsUntilDistValid = true;
      this._yearsUntilDistErrorMessage = '';
      this.sharedInfoService.removeInputError('Years until distribution');
      this.sharedInfoService.setYearsUntilDist(value);
    }
  }

  // eslint-disable-next-line max-statements
  onPaymentMethodChange(value: string): void {
    const previousPaymentMethod = this._paymentMethod;
    this._paymentMethod = value;

    if (previousPaymentMethod === 'cash' && value !== 'cash' && !this.cashYieldValid) {
      const defaultCashYield = this.sharedInfoService.defaultInputData.cashYield;
      this._cashYield = defaultCashYield;
      this.cashYieldValid = true;
      this._cashYieldErrorMessage = '';
      this.sharedInfoService.removeInputError('Cash yield');
      this.sharedInfoService.setCashYield(defaultCashYield);
    }

    if (previousPaymentMethod === 'tax-efficient' && value !== 'tax-efficient') {
      if (!this.dividendTaxRateValid) {
        const defaultDividendTaxRate = this.sharedInfoService.defaultInputData.dividendTaxRate;
        this._dividendTaxRate = defaultDividendTaxRate;
        this.dividendTaxRateValid = true;
        this._dividendTaxRateErrorMessage = '';
        this.sharedInfoService.removeInputError('Dividend tax rate');
        this.sharedInfoService.setDividendTaxRate(defaultDividendTaxRate);
      }

      if (!this.capitalGainsTaxRateValid) {
        const defaultCapitalGainsTaxRate =
          this.sharedInfoService.defaultInputData.capitalGainsTaxRate;
        this._capitalGainsTaxRate = defaultCapitalGainsTaxRate;
        this.capitalGainsTaxRateValid = true;
        this._capitalGainsTaxRateErrorMessage = '';
        this.sharedInfoService.removeInputError('Capital gains tax rate');
        this.sharedInfoService.setCapitalGainsTaxRate(defaultCapitalGainsTaxRate);
      }

      if (!this.dividendYieldValid) {
        const defaultDividendYield = this.sharedInfoService.defaultInputData.dividendYield;
        this._dividendYield = defaultDividendYield;
        this.dividendYieldValid = true;
        this._dividendYieldErrorMessage = '';
        this.sharedInfoService.removeInputError('Dividend yield');
        this.sharedInfoService.setDividendYield(defaultDividendYield);
      }
    }

    this.sharedInfoService.setHowWillYouPay(value);
    this.compareAnnualReturnDividendYield();
  }

  onAnnualReturnChange(value: number): void {
    this._annualReturn = value;
    // VALIDATION: Check if value is between 1% and 50%
    if (isNaN(value) || value < this.annualReturnMin || value > this.annualReturnMax) {
      this.annualReturnValid = false;
      this._annualReturnErrorMessage = this.errorsContent?.['rothannualreturnerror'];
      this.sharedInfoService.addInputError('Annual return');
    } else {
      this.annualReturnValid = true;
      this._annualReturnErrorMessage = '';
      this.sharedInfoService.removeInputError('Annual return');
      this.sharedInfoService.setAnnualReturn(value);
      // Clear stale cross-validation error on dividend yield if it passes its own range check
      if (
        !isNaN(this._dividendYield) &&
        this._dividendYield >= this.dividendYieldMin &&
        this._dividendYield <= this.dividendYieldMax
      ) {
        this.dividendYieldValid = true;
        this._dividendYieldErrorMessage = '';
        this.sharedInfoService.removeInputError('Dividend yield');
      }
    }
    this.compareAnnualReturnDividendYield();
  }

  onCashYieldChange(value: number): void {
    this._cashYield = value;
    // VALIDATION: Check if value is between 0% and 10%
    if (isNaN(value) || value < this.cashYieldMin || value > this.cashYieldMax) {
      this.cashYieldValid = false;
      this._cashYieldErrorMessage = this.errorsContent?.['rothcashyealderror'];
      this.sharedInfoService.addInputError('Cash yield');
    } else {
      this.cashYieldValid = true;
      this._cashYieldErrorMessage = '';
      this.sharedInfoService.removeInputError('Cash yield');
      this.sharedInfoService.setCashYield(value);
    }
  }

  onDividendTaxRateChange(value: number): void {
    this._dividendTaxRate = value;
    // VALIDATION: Check if value is between 0% and 100%
    if (isNaN(value) || value < this.dividendTaxRateMin || value > this.dividendTaxRateMax) {
      this.dividendTaxRateValid = false;
      this._dividendTaxRateErrorMessage = this.errorsContent?.['rothdividenttaxerror'];
      this.sharedInfoService.addInputError('Dividend tax rate');
    } else {
      this.dividendTaxRateValid = true;
      this._dividendTaxRateErrorMessage = '';
      this.sharedInfoService.removeInputError('Dividend tax rate');
      this.sharedInfoService.setDividendTaxRate(value);
    }
  }

  onCapitalGainsTaxRateChange(value: number): void {
    this._capitalGainsTaxRate = value;
    // VALIDATION: Check if value is between 0% and 100%
    if (
      isNaN(value) ||
      value < this.capitalGainsTaxRateMin ||
      value > this.capitalGainsTaxRateMax
    ) {
      this.capitalGainsTaxRateValid = false;
      this._capitalGainsTaxRateErrorMessage = this.errorsContent?.['rothcapitalgainerror'];
      this.sharedInfoService.addInputError('Capital gains tax rate');
    } else {
      this.capitalGainsTaxRateValid = true;
      this._capitalGainsTaxRateErrorMessage = '';
      this.sharedInfoService.removeInputError('Capital gains tax rate');
      this.sharedInfoService.setCapitalGainsTaxRate(value);
    }
  }

  onDividendYieldChange(value: number): void {
    this._dividendYield = value;
    // VALIDATION: Check if value is between 0% and 10%
    if (isNaN(value) || value < this.dividendYieldMin || value > this.dividendYieldMax) {
      this.dividendYieldValid = false;
      this._dividendYieldErrorMessage = this.errorsContent?.['rothdividendyielderror'];
      this.sharedInfoService.addInputError('Dividend yield');
    } else {
      this.dividendYieldValid = true;
      this._dividendYieldErrorMessage = '';
      this.sharedInfoService.removeInputError('Dividend yield');
      this.sharedInfoService.setDividendYield(value);
      // Clear stale cross-validation error on annual return if it passes its own range check
      if (
        !isNaN(this._annualReturn) &&
        this._annualReturn >= this.annualReturnMin &&
        this._annualReturn <= this.annualReturnMax
      ) {
        this.annualReturnValid = true;
        this._annualReturnErrorMessage = '';
        this.sharedInfoService.removeInputError('Annual return');
      }
    }
    this.compareAnnualReturnDividendYield();
  }

  onPercentOfIraChange(value: number): void {
    this._percentOfIra = value;
    // VALIDATION: Check if value is between 0% and 100%
    if (isNaN(value) || value < this.percentOfIraMin || value > this.percentOfIraMax) {
      this.percentOfIraValid = false;
      this._percentOfIraErrorMessage = this.errorsContent?.['rothirataxerror'];
      this.sharedInfoService.addInputError('Percent of IRA already taxed');
    } else {
      this.percentOfIraValid = true;
      this._percentOfIraErrorMessage = '';
      this.sharedInfoService.removeInputError('Percent of IRA already taxed');
      this.sharedInfoService.setPercentOfIra(value);
    }
  }

  private compareAnnualReturnDividendYield(): void {
    // Cross-validation only applies to tax-efficient payment method
    if (this._paymentMethod !== 'tax-efficient') {
      this.clearCrossValidationErrors();
      return;
    }
    // Only cross-validate when both fields individually pass their range checks. A blank annual return
    // from Clear all fields has nothing to compare.
    if (
      !this.annualReturnValid ||
      !this.dividendYieldValid ||
      this.sharedInfoService.isInputCleared('annualReturn')
    ) {
      return;
    }
    if (this._annualReturn < this._dividendYield) {
      this.annualReturnValid = false;
      this._annualReturnErrorMessage = this.rothConvDetails?.annualReturnError;
      this.sharedInfoService.addInputError('Annual return');

      this.dividendYieldValid = false;
      this._dividendYieldErrorMessage = this.rothConvDetails?.dividendYieldError;
      this.sharedInfoService.addInputError('Dividend yield');
    }
  }

  private clearCrossValidationErrors(): void {
    // Clear any stale cross-validation errors on annual return and dividend yield
    if (
      this.annualReturnValid === false &&
      !isNaN(this._annualReturn) &&
      this._annualReturn >= this.annualReturnMin &&
      this._annualReturn <= this.annualReturnMax
    ) {
      this.annualReturnValid = true;
      this._annualReturnErrorMessage = '';
      this.sharedInfoService.removeInputError('Annual return');
    }
    if (
      this.dividendYieldValid === false &&
      !isNaN(this._dividendYield) &&
      this._dividendYield >= this.dividendYieldMin &&
      this._dividendYield <= this.dividendYieldMax
    ) {
      this.dividendYieldValid = true;
      this._dividendYieldErrorMessage = '';
      this.sharedInfoService.removeInputError('Dividend yield');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  get rothConversionAmountHintText(): string {
    if (this.stateTaxRatesFromBXR) {
      return this.rothConversionAmountHelperText;
    }

    return this._paymentMethod === 'cash'
      ? this.rothConvDetails?.rothConvCashLabel
      : this.rothConvDetails?.rothConvLabel;
  }

  get rothConversionAmountErrorMessage(): string {
    return this.rothConversionAmountValid ? '' : this._rothConversionAmountErrorMessage;
  }

  get yearsUntilDistErrorMessage(): string {
    return this.yearsUntilDistValid ? '' : this._yearsUntilDistErrorMessage;
  }

  get annualReturnErrorMessage(): string {
    return this.annualReturnValid ? '' : this._annualReturnErrorMessage;
  }

  get cashYieldErrorMessage(): string {
    return this.cashYieldValid ? '' : this._cashYieldErrorMessage;
  }

  get dividendTaxRateErrorMessage(): string {
    return this.dividendTaxRateValid ? '' : this._dividendTaxRateErrorMessage;
  }

  get capitalGainsTaxRateErrorMessage(): string {
    return this.capitalGainsTaxRateValid ? '' : this._capitalGainsTaxRateErrorMessage;
  }

  get dividendYieldErrorMessage(): string {
    return this.dividendYieldValid ? '' : this._dividendYieldErrorMessage;
  }

  get percentOfIraErrorMessage(): string {
    return this.percentOfIraValid ? '' : this._percentOfIraErrorMessage;
  }

  get rateDiffersText(): string {
    return this.errorsContent?.['rateDiffers'];
  }

  get rateDiffersLinkText(): string {
    return this.errorsContent?.['rateDiffersLink'];
  }
}
